import React, { useState, useEffect } from "react";
import FormWizard from "react-form-wizard-component";
import "react-form-wizard-component/dist/style.css";
import { SupabaseService } from "../services/SupabaseService";

const AppointmentForm: React.FC = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    vin_number: "",
    phone_number: "",
    email: "",
    contact_method_text: false,
    contact_method_email: false,
    contact_method_call: false,
    appointment_date: "",
    appointment_time: "",
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [stepSize, setStepSize] = useState("lg"); // Default to large for bigger screens
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [showButtons, setShowButtons] = useState(true); // New state to control button visibility

  useEffect(() => {
    const handleResize = () => {
      setStepSize(window.innerWidth < 768 ? "xs" : "lg");
    };

    // Set initial step size based on current window width
    handleResize();

    // Add event listener for resize
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleComplete = () => {
    console.log("Form completed!");
    // Handle form completion logic here
    SupabaseService.sendFormData(formData).then(() => {
      setFormSubmitted(true);
      setIsFormDisabled(true); // Disable the form after submission
      setShowButtons(false); // Hide buttons after submission
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formSubmitted) { // Only allow changes if the form has not been submitted
      const { name, value, type, checked } = e.target;
      setFormData((prevData) => ({
        ...prevData,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  return (
    <div id="appointment-form" style={{ overflowY: "auto" }}>
      <FormWizard
        shape="circle"
        color="#e00003" // Custom color
        onComplete={handleComplete}
        stepSize={stepSize} // Set step size dynamically
        backButtonText="Back"
        nextButtonText="Next"
        finishButtonText="Finish"
        disabled={() => isFormDisabled} // Pass a function that returns the value
      >
        <FormWizard.TabContent title="Personal Information" icon="ti-user">
          <div className="input-wrapper">
            <label htmlFor="full_name">
              <i className="ti-user"></i> Full Name
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              placeholder="Enter your full name"
              className="styled-input"
              value={formData.full_name}
              onChange={handleInputChange}
              disabled={isFormDisabled} // Disable the input if form is disabled
            />
          </div>
          <div className="input-wrapper">
            <label htmlFor="vin_number">
              <i className="ti-car"></i> VIN Number
            </label>
            <input
              type="text"
              id="vin_number"
              name="vin_number"
              placeholder="Enter your VIN number"
              className="styled-input"
              value={formData.vin_number}
              onChange={handleInputChange}
              disabled={isFormDisabled} // Disable the input if form is disabled
            />
          </div>
          <div className="input-wrapper">
            <label htmlFor="phone_number">
              <i className="ti-mobile"></i> Phone Number
            </label>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              placeholder="Enter your phone number"
              className="styled-input"
              value={formData.phone_number}
              onChange={handleInputChange}
              disabled={isFormDisabled} // Disable the input if form is disabled
            />
          </div>
          <div className="input-wrapper">
            <label htmlFor="email">
              <i className="ti-email"></i> Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Enter your email address"
              className="styled-input"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isFormDisabled} // Disable the input if form is disabled
            />
          </div>
        </FormWizard.TabContent>
        <FormWizard.TabContent title="Appointment Info" icon="ti-calendar">
          <h3>Appointment Details</h3>
          <div className="input-wrapper">
            <label htmlFor="appointment_date">
              <i className="ti-calendar"></i> Select Date
            </label>
            <input
              type="date"
              id="appointment_date"
              name="appointment_date"
              placeholder="Select Date"
              className="styled-input"
              value={formData.appointment_date}
              onChange={handleInputChange}
              disabled={isFormDisabled} // Disable the input if form is disabled
            />
          </div>
          <div className="input-wrapper">
            <label htmlFor="appointment_time">
              <i className="ti-alarm-clock"></i> Select Time
            </label>
            <input
              type="time"
              id="appointment_time"
              name="appointment_time"
              placeholder="Select Time"
              className="styled-input"
              value={formData.appointment_time}
              onChange={handleInputChange}
              disabled={isFormDisabled} // Disable the input if form is disabled
            />
          </div>
          <div className="input-wrapper">
            <label>How do you prefer to be contacted?</label>
            <div>
              <input
                type="checkbox"
                id="contact_method_text"
                name="contact_method_text"
                checked={formData.contact_method_text}
                onChange={handleInputChange}
                disabled={isFormDisabled} // Disable the checkbox if form is disabled
              />
              <label htmlFor="contact_method_text">Text Message</label>
            </div>
            <div>
              <input
                type="checkbox"
                id="contact_method_email"
                name="contact_method_email"
                checked={formData.contact_method_email}
                onChange={handleInputChange}
                disabled={isFormDisabled} // Disable the checkbox if form is disabled
              />
              <label htmlFor="contact_method_email">Email</label>
            </div>
            <div>
              <input
                type="checkbox"
                id="contact_method_call"
                name="contact_method_call"
                checked={formData.contact_method_call}
                onChange={handleInputChange}
                disabled={isFormDisabled} // Disable the checkbox if form is disabled
              />
              <label htmlFor="contact_method_call">Phone Call</label>
            </div>
          </div>
        </FormWizard.TabContent>
        <FormWizard.TabContent title="Confirmation" icon="ti-check">
          {formSubmitted ? (
            <div className="confirmation-message">
              <div className="check-icon">
                <i className="ti-check"></i>
              </div>
              <p style={{ textAlign: "center" }}>
                Form submitted successfully!
              </p>
            </div>
          ) : (
            <div className="confirmation-details">
              <h3>
                <strong>Confirm Appointment</strong>
              </h3>
              <h4>
                <strong>Details:</strong>
              </h4>
              <p>
                <strong>Full Name:</strong> {formData.full_name}
              </p>
              <p>
                <strong>VIN Number:</strong> {formData.vin_number}
              </p>
              <p>
                <strong>Phone Number:</strong> {formData.phone_number}
              </p>
              <p>
                <strong>Appointment Date:</strong> {formData.appointment_date}
              </p>
              <p>
                <strong>Appointment Time:</strong> {formData.appointment_time}
              </p>
              <p>
                <strong>Email:</strong> {formData.email}
              </p>
              <p>
                <strong>Contact Methods:</strong>{" "}
                {formData.contact_method_text ? "Text Message," : ""}{" "}
                {formData.contact_method_email ? "Email," : ""}{" "}
                {formData.contact_method_call ? "Phone Call" : ""}
              </p>
            </div>
          )}
        </FormWizard.TabContent>
      </FormWizard>

      <style>{`
        /* Your custom styles */
        @import url("https://cdn.jsdelivr.net/gh/lykmapipo/themify-icons@0.1.2/css/themify-icons.css");

        #appointment-form {
          max-width: 100%;
          overflow: hidden;
        }

        .wizard-card-footer {
          display: ${showButtons ? "flex" : "none"}; /* Conditionally hide/show buttons */
          justify-content: center;
          margin-top: 60px;
          align-items: center;
        }

        .wizard-card-footer .wizard-btn {
          background-color: #e00003;
          color: white;
          padding: 10px 20px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .wizard-card-footer .wizard-btn:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }

        .wizard-card-footer .wizard-btn:hover:not(:disabled) {
          background-color: #a00002;
        }

        .input-wrapper {
          margin-bottom: 20px;
        }

        .styled-input {
          width: 100%;
          padding: 10px;
          font-size: 16px;
          border: 1px solid #ccc;
          border-radius: 5px;
          box-sizing: border-box;
          transition: border-color 0.3s ease;
        }

        .styled-input:focus {
          outline: none;
          border-color: #e00003;
        }

        .input-wrapper label {
          display: block;
          margin-bottom: 5px;
        }

        .input-wrapper i {
          margin-right: 5px;
        }

        /* Additional styles for checkbox alignment */
        .input-wrapper div {
          display: inline-block;
          margin-right: 20px;
        }

        .confirmation-details {
          display: flex;
          flex-direction: column;
          flex-wrap: wrap;
          align-content: flex-start;
          justify-content: center;
          align-items: flex-start;
        }

        .confirmation-message {
          display: flex;
          flex-direction: column;
          flex-wrap: wrap;
          align-content: space-around;
          justify-content: space-between;
          align-items: center;
        }

        /* Styles for the check icon */
        .check-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 20px;
          background: green;
          border-radius: 50%;
          width: 70px;
          height: 70px;
          flex-wrap: wrap;
          flex-direction: column;
          align-content: stretch;
        }
        
        .check-icon i {
          font-size: 48px;
          color: white;
        }

        /* Responsive styles */
        @media (max-width: 768px) {
          display: ${showButtons ? "flex" : "none"};
          .input-wrapper {
            margin-bottom: 10px;
          }

          .styled-input {
            padding: 8px;
            font-size: 14px;
          }

          .confirmation-details p {
            font-size: 14px;
          }

          .check-icon {
            width: 50px;
            height: 50px;
          }

          .check-icon i {
            font-size: 32px;
          }
        }

        @media (max-width: 480px) {
          display: ${showButtons ? "flex" : "none"};
          .wizard-card-footer {
            display: flex;
            justify-content: center;
            margin-top: 60px;
            align-items: center;
          }

          .input-wrapper div {
            display: block;
            margin-right: 0;
            margin-bottom: 10px;
          }
        }

        /* Hide scrollbar but keep scrolling */
        #appointment-form::-webkit-scrollbar {
          width: 0;
        }

        #appointment-form {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }
      `}</style>
      
    </div>
  );
};

export default AppointmentForm;
